import { useEffect, useState } from 'react';
import { diffProjectSnapshots } from '../../api/projects';
import { explainDiff } from '../../api/ai';
import { useEstimationStore } from '../../stores/estimationStore';
import type { VersionDiff } from '../../types/estimation';
import styles from './VersionDiffView.module.css';

interface Props {
  projectId: string;
  versionA: number;
  versionB: number;
}

export function VersionDiffView({ projectId, versionA, versionB }: Props) {
  const [diff, setDiff] = useState<VersionDiff | null>(null);
  const [loading, setLoading] = useState(true);
  const [aiExplanation, setAiExplanation] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiGenerated, setAiGenerated] = useState(false);
  const [aiError, setAiError] = useState(false);

  const ha = useEstimationStore((s) => s.ha);
  const haZi = useEstimationStore((s) => s.haZi);
  const niveau = useEstimationStore((s) => s.niveau);

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setAiExplanation(null);
        setAiError(false);
        const data = await diffProjectSnapshots(projectId, versionA, versionB);
        setDiff(data);
      } catch (err) {
        console.error('Failed to load diff:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [projectId, versionA, versionB]);

  async function handleExplain() {
    if (!diff || aiLoading) return;
    try {
      setAiLoading(true);
      setAiError(false);
      const result = await explainDiff({
        session_id: projectId,
        version_a: versionA,
        version_b: versionB,
        diff_changes: diff.changes,
        ha,
        ha_zi: haZi,
        niveau,
      });
      setAiExplanation(result.explanation);
      setAiGenerated(result.ai_generated);
    } catch {
      setAiError(true);
    } finally {
      setAiLoading(false);
    }
  }

  if (loading) return <div className={styles.status}>Loading diff...</div>;
  if (!diff) return <div className={styles.status}>Failed to load diff.</div>;

  const changeKeys = Object.keys(diff.changes);

  if (changeKeys.length === 0) {
    return (
      <div className={styles.empty}>
        No differences between v{versionA} and v{versionB}.
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h3 className={styles.title}>
        Diff: v{versionA} vs v{versionB}
      </h3>

      {/* AI Explanation Section */}
      <div className={styles.aiSection}>
        {!aiExplanation && !aiLoading && (
          <button
            className={styles.explainBtn}
            onClick={handleExplain}
            disabled={aiLoading}
          >
            <span className={styles.sparkle}>&#x2728;</span>
            Explain changes with AI
          </button>
        )}

        {aiLoading && (
          <div className={styles.aiLoading}>
            <div className={styles.aiLoadingDots}>
              <span />
              <span />
              <span />
            </div>
            <span>Analyzing changes...</span>
          </div>
        )}

        {aiError && (
          <div className={styles.aiError}>
            Failed to generate explanation.{' '}
            <button className={styles.retryLink} onClick={handleExplain}>
              Retry
            </button>
          </div>
        )}

        {aiExplanation && (
          <div className={styles.aiExplanation}>
            <div className={styles.aiHeader}>
              <span className={styles.sparkle}>&#x2728;</span>
              <span className={styles.aiLabel}>
                AI Analysis
              </span>
              {!aiGenerated && (
                <span className={styles.fallbackBadge}>Basic</span>
              )}
            </div>
            <p className={styles.aiText}>{aiExplanation}</p>
          </div>
        )}
      </div>

      {/* Raw Diff Table */}
      <table className={styles.table}>
        <thead>
          <tr className={styles.headerRow}>
            <th className={styles.th}>Field</th>
            <th className={`${styles.th} ${styles.oldCol}`}>v{versionA}</th>
            <th className={`${styles.th} ${styles.newCol}`}>v{versionB}</th>
            <th className={styles.th}>Delta</th>
          </tr>
        </thead>
        <tbody>
          {changeKeys.map((key) => {
            const entry = diff.changes[key];
            if (!entry) return null;
            const oldVal = entry.old;
            const newVal = entry.new;
            let delta = '';
            if (typeof oldVal === 'number' && typeof newVal === 'number' && oldVal !== 0) {
              const pct = ((newVal - oldVal) / Math.abs(oldVal)) * 100;
              delta = `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`;
            }
            return (
              <tr key={key} className={styles.row}>
                <td className={styles.fieldName}>{key}</td>
                <td className={styles.oldVal}>
                  {formatDiffValue(oldVal)}
                </td>
                <td className={styles.newVal}>
                  {formatDiffValue(newVal)}
                </td>
                <td className={`${styles.delta} ${delta.startsWith('+') ? styles.deltaUp : delta.startsWith('-') ? styles.deltaDown : ''}`}>
                  {delta}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function formatDiffValue(val: unknown): string {
  if (val === null || val === undefined) return '-';
  if (typeof val === 'number') {
    return val.toLocaleString('fr-CH');
  }
  if (typeof val === 'object') {
    return JSON.stringify(val);
  }
  return String(val);
}
